import { apiGetJSON } from "../client.js";
import { composeAbortSignal, isAbortError, requestsCurrent, startRequest } from "../core/request-coordinator.js";
import { isTerminalRunState } from "../domain/conversation.js";
import { normalizeThreadReadModel } from "../domain/readmodels.js";
import { enrichHarnessThreadSnapshot } from "./harness/snapshot.js";
import { loadLLMThreadContextItems } from "./llm/context-items.js";
import { enrichLLMThreadSnapshot } from "./llm/snapshot.js";
import { mergeThreadRefreshState, resetThreadComponentState } from "./state.js";

export async function loadThreadComponent(state, apiKey, threadID, options = {}) {
  if (!apiKey) {
    resetThreadComponentState(state);
    return false;
  }

  const snapshotRequest = startRequest(state.requests.snapshot);
  const signal = composeAbortSignal(snapshotRequest.signal, options.signal, options.request?.signal);
  try {
    const snapshot = await fetchThreadComponentSnapshot(apiKey, threadID, {
      signal,
      includeDiagnostics: options.includeDiagnostics
    });
    if (!requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    const nextThread = snapshot.thread;
    const preserveComposerState = state.thread?.id === nextThread.id;
    const nextDraft = preserveComposerState ? state.composerDraft : "";
    const nextCapability = preserveComposerState ? state.composerCapability : "responses";
    const nextRouteOverride = preserveComposerState ? state.composerRouteOverride : "";
    const nextModelOverride = preserveComposerState ? state.composerModelOverride : "";
    const nextContextRefs = preserveComposerState ? [...(state.composerContextRefs || [])] : [];
    const nextContextPanelOpen = preserveComposerState ? Boolean(state.composerContextPanelOpen) : false;
    const nextContextItems = preserveComposerState ? [...(state.contextItems || [])] : [];
    const nextContextItemsLoadedThreadID = preserveComposerState ? String(state.contextItemsLoadedThreadID || "") : "";
    const nextContextItemsLoading = preserveComposerState ? Boolean(state.contextItemsLoading) : false;
    const nextContextItemsError = preserveComposerState ? String(state.contextItemsError || "") : "";
    const nextContextItemFilterOpen = preserveComposerState ? Boolean(state.contextItemFilterOpen) : false;
    const nextContextItemFilterText = preserveComposerState ? String(state.contextItemFilterText || "") : "";
    const nextContextItemDraftTitle = preserveComposerState ? String(state.contextItemDraftTitle || "") : "";
    const nextContextItemDraftText = preserveComposerState ? String(state.contextItemDraftText || "") : "";
    const nextContextItemDraftContentType = preserveComposerState ? String(state.contextItemDraftContentType || "text/plain") : "text/plain";
    const nextContextItemUploadFilename = preserveComposerState ? String(state.contextItemUploadFilename || "") : "";
    const nextContextItemDeleteConfirmID = preserveComposerState ? String(state.contextItemDeleteConfirmID || "") : "";
    const nextContextItemPreviewID = preserveComposerState ? String(state.contextItemPreviewID || "") : "";
    const nextContextItemPreviewLoading = preserveComposerState ? Boolean(state.contextItemPreviewLoading) : false;
    const nextContextItemPreviewError = preserveComposerState ? String(state.contextItemPreviewError || "") : "";
    const nextPendingAttachments = preserveComposerState ? [...(state.pendingAttachments || [])] : [];
    const nextCaptureState = preserveComposerState ? { ...(state.captureState || {}) } : { kind: "", status: "idle", source: "" };
    resetThreadComponentState(state, {
      cancelRequests: false,
      preserveTransientMedia: preserveComposerState,
      preserveObjectURLs: preserveComposerState ? preservedObjectURLs(nextContextRefs, nextPendingAttachments) : null
    });
    if (!requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    state.thread = nextThread;
    state.workspace = snapshot.workspace;
    state.workspaceDiagnostics = snapshot.workspaceDiagnostics;
    state.threadDiagnostics = snapshot.threadDiagnostics;
    state.turns = snapshot.turns;
    state.decisionsByTurnID = snapshot.decisionsByTurnID;
    state.eventsByTurnID = snapshot.eventsByTurnID;
    state.eventSeqsByTurnID = snapshot.eventSeqsByTurnID;
    state.composerDraft = nextDraft;
    state.composerCapability = nextCapability;
    state.composerRouteOverride = nextRouteOverride;
    state.composerModelOverride = nextModelOverride;
    state.composerContextRefs = nextContextRefs;
    state.composerContextPanelOpen = nextContextPanelOpen;
    state.contextItems = nextContextItems;
    state.contextItemsLoadedThreadID = nextContextItemsLoadedThreadID;
    state.contextItemsLoading = nextContextItemsLoading;
    state.contextItemsError = nextContextItemsError;
    state.contextItemFilterOpen = nextContextItemFilterOpen;
    state.contextItemFilterText = nextContextItemFilterText;
    state.contextItemDraftTitle = nextContextItemDraftTitle;
    state.contextItemDraftText = nextContextItemDraftText;
    state.contextItemDraftContentType = nextContextItemDraftContentType;
    state.contextItemUploadFilename = nextContextItemUploadFilename;
    state.contextItemDeleteConfirmID = nextContextItemDeleteConfirmID;
    state.contextItemPreviewID = nextContextItemPreviewID;
    state.contextItemPreviewLoading = nextContextItemPreviewLoading;
    state.contextItemPreviewError = nextContextItemPreviewError;
    state.pendingAttachments = nextPendingAttachments;
    state.captureState = nextCaptureState;
    if (String(state.thread?.mode || "").trim().toLowerCase() === "llm") {
      await loadLLMThreadContextItems(state, apiKey, {
        contextPermissions: options.contextPermissions,
        parentRequest: options.request,
        request: snapshotRequest,
        signal
      });
      if (!requestsCurrent(snapshotRequest, options.request)) {
        return false;
      }
    }
    return true;
  } catch (error) {
    if (isAbortError(error) || !requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    if (String(error.message || "").includes("session not found") || String(error.message || "").includes("thread not found")) {
      resetThreadComponentState(state, { cancelRequests: false });
      state.notFound = true;
      return true;
    }
    throw error;
  }
}

export async function refreshThreadComponent(state, apiKey, threadID, options = {}) {
  if (!apiKey) {
    return false;
  }

  const snapshotRequest = startRequest(state.requests.snapshot);
  const signal = composeAbortSignal(snapshotRequest.signal, options.signal, options.request?.signal);
  try {
    const snapshot = await fetchThreadRefreshSnapshot(state, apiKey, threadID, options, signal);
    if (!requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    mergeThreadRefreshState(state, snapshot);
    return true;
  } catch (error) {
    if (isAbortError(error) || !requestsCurrent(snapshotRequest, options.request)) {
      return false;
    }
    throw error;
  }
}

export async function fetchThreadComponentSnapshot(apiKey, threadID, options = {}) {
  const snapshot = normalizeThreadReadModel(await apiGetJSON(apiKey, `/internal/readmodels/threads/${encodeURIComponent(threadID)}`, { signal: options.signal }));
  return enrichThreadSnapshot(apiKey, snapshot, threadID, options);
}

export async function fetchThreadRefreshSnapshot(state, apiKey, threadID, options = {}, signal = null) {
  const snapshot = normalizeThreadReadModel(await apiGetJSON(apiKey, `/internal/readmodels/threads/${encodeURIComponent(threadID)}`, { signal }));
  return enrichThreadSnapshot(apiKey, snapshot, threadID, {
    ...options,
    signal
  });
}

export function shouldRefreshThreadEvents(item, forceTurnIDs) {
  if (!item || !item.id) {
    return false;
  }
  if (forceTurnIDs.has(item.id)) {
    return true;
  }
  if (!isTerminalRunState(String(item.state || "").trim())) {
    return true;
  }
  return String(item.wait_state || "").trim() === "awaiting_decision";
}

async function enrichThreadSnapshot(apiKey, snapshot, threadID, options = {}) {
  if (String(snapshot?.thread?.mode || "").trim().toLowerCase() === "llm") {
    return enrichLLMThreadSnapshot(apiKey, snapshot, threadID, options);
  }
  return enrichHarnessThreadSnapshot(apiKey, snapshot, threadID, options.signal);
}

function preservedObjectURLs(contextRefs, pendingAttachments) {
  const urls = new Set();
  for (const ref of contextRefs || []) {
    const objectURL = String(ref?.attachment?.object_url || "").trim();
    if (objectURL) {
      urls.add(objectURL);
    }
  }
  for (const item of pendingAttachments || []) {
    const objectURL = String(item?.object_url || "").trim();
    if (objectURL) {
      urls.add(objectURL);
    }
  }
  return urls;
}
